/**
 * VersionHistoryPanel — unified History tab with three sub-views:
 *   - Graph: SVG git-style branch graph (GitGraph)
 *   - Events: semantic audit trail (ChangelogPanel)
 *   - Branches: branch CRUD, diff, merge (BranchManagementPanel)
 *
 * Toggled via a segmented control at the top.
 */

import { useState } from "react";
import { Badge, Box, Flex, Heading } from "@radix-ui/themes";

import { useProvenanceContext } from "../../context/ProvenanceContext";
import { GitGraph } from "./GitGraph";
import { ChangelogPanel } from "../Changelog";
import { BranchManagementPanel } from "./BranchManagementPanel";

type Segment = "graph" | "events" | "branches";

const SEGMENTS: { id: Segment; label: string }[] = [
  { id: "graph", label: "Graph" },
  { id: "events", label: "Events" },
  { id: "branches", label: "Branches" },
];

interface Props {
  projectId: string;
}

export function VersionHistoryPanel({ projectId }: Props) {
  const { currentBranch } = useProvenanceContext();
  const [active, setActive] = useState<Segment>("graph");

  return (
    <Box style={{ height: "100%", display: "flex", flexDirection: "column" }}>
      {/* Header */}
      <Box p="3" pb="0">
        <Flex align="center" justify="between" mb="3">
          <Heading size="3">History</Heading>
          <Badge size="1" color={currentBranch === "main" ? "blue" : "orange"}>
            {currentBranch}
          </Badge>
        </Flex>

        {/* Segmented control */}
        <Flex
          gap="0"
          style={{
            borderRadius: 6,
            border: "1px solid var(--gray-a5)",
            overflow: "hidden",
            background: "var(--gray-a2)",
          }}
        >
          {SEGMENTS.map((seg) => {
            const isActive = active === seg.id;
            return (
              <button
                key={seg.id}
                data-testid={`segment-${seg.id}`}
                onClick={() => setActive(seg.id)}
                style={{
                  flex: 1,
                  padding: "6px 0",
                  fontSize: 11,
                  fontWeight: isActive ? 600 : 400,
                  color: isActive ? "var(--accent-11)" : "var(--gray-11)",
                  background: isActive ? "var(--accent-a3)" : "transparent",
                  border: "none",
                  cursor: "pointer",
                  fontFamily: "inherit",
                  transition: "background 120ms, color 120ms",
                }}
              >
                {seg.label}
              </button>
            );
          })}
        </Flex>
      </Box>

      {/* Content area */}
      <Box style={{ flex: 1, minHeight: 0, display: "flex", flexDirection: "column" }}>
        {active === "graph" && <GitGraph projectId={projectId} />}
        {active === "events" && <ChangelogPanel projectId={projectId} />}
        {active === "branches" && <BranchManagementPanel projectId={projectId} />}
      </Box>
    </Box>
  );
}
